import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Image } from '@imagekit/react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import Navbar from './Navbar';
import Footer from './Footer';
import { mediaUrl } from '../utils/media';
import {
  defaultEventAlbumId,
  eventVideoCount,
  loadEventAlbums,
  EventAlbum,
  EventMedia,
} from '../data/events';

const PAGE_SIZE = 12;

const EventsPage: React.FC = () => {
  const galleryQuery = useQuery({
    queryKey: ['event-images'],
    queryFn: loadEventAlbums,
  });
  const eventAlbums = useMemo(() => galleryQuery.data?.albums ?? [], [galleryQuery.data]);
  const eventPhotoCount = galleryQuery.data?.photoCount ?? 0;
  const galleryError = galleryQuery.error instanceof Error ? galleryQuery.error.message : '';
  const isLoadingImages = galleryQuery.isFetching && !galleryQuery.data;
  const [activeAlbumId, setActiveAlbumId] = useState<string>(defaultEventAlbumId);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [activeMedia, setActiveMedia] = useState<EventMedia | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const activeAlbum = useMemo<EventAlbum | undefined>(
    () => eventAlbums.find((album) => album.id === activeAlbumId) || eventAlbums[0],
    [activeAlbumId, eventAlbums]
  );

  const visibleItems = activeAlbum?.items.slice(0, visibleCount) || [];
  const hasMore = Boolean(activeAlbum && visibleCount < activeAlbum.items.length);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false,
    });

    const backToTop = document.querySelector('.back-to-top');
    if (backToTop) {
      const handleScroll = () => {
        if (window.scrollY > 300) {
          (backToTop as HTMLElement).style.display = 'block';
        } else {
          (backToTop as HTMLElement).style.display = 'none';
        }
      };

      const handleBackToTopClick = (e: Event) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      };

      window.addEventListener('scroll', handleScroll);
      backToTop.addEventListener('click', handleBackToTopClick);

      return () => {
        window.removeEventListener('scroll', handleScroll);
        backToTop.removeEventListener('click', handleBackToTopClick);
      };
    }
  }, []);

  useEffect(() => {
    if (isLoadingImages) {
      return;
    }
    const spinner = document.getElementById('spinner');
    if (spinner) {
      spinner.classList.remove('show');
    }
  }, [isLoadingImages]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setActiveMedia(null);
  }, [activeAlbumId]);

  const showLightboxAt = useCallback((index: number) => {
    if (!activeAlbum) {
      return;
    }
    const nextIndex = (index + activeAlbum.items.length) % activeAlbum.items.length;
    setLightboxIndex(nextIndex);
    setActiveMedia(activeAlbum.items[nextIndex]);
  }, [activeAlbum]);

  useEffect(() => {
    if (!activeMedia || !activeAlbum) {
      document.body.style.overflow = '';
      return undefined;
    }

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveMedia(null);
        return;
      }
      if (event.key === 'ArrowRight') {
        showLightboxAt(lightboxIndex + 1);
      }
      if (event.key === 'ArrowLeft') {
        showLightboxAt(lightboxIndex - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeMedia, activeAlbum, lightboxIndex, showLightboxAt]);

  const openMedia = (media: EventMedia) => {
    if (!activeAlbum) {
      return;
    }
    setActiveMedia(media);
    setLightboxIndex(activeAlbum.items.findIndex((item) => item.id === media.id));
  };

  const closeLightbox = () => {
    setActiveMedia(null);
  };

  return (
    <>
      <div
        id="spinner"
        className="show bg-white position-fixed translate-middle w-100 vh-100 top-50 start-50 d-flex align-items-center justify-content-center"
      >
        <div className="spinner-grow text-primary" role="status"></div>
      </div>

      <Navbar />

      <div
        className="container-fluid page-header events-page-header mb-5"
        data-aos="fade"
        data-aos-delay="100"
        style={{
          background: `linear-gradient(rgba(0, 29, 35, .5), rgba(0, 29, 35, .5)), url(${mediaUrl('events-header.jpg')}) center center / cover no-repeat`,
        }}
      >
        <div className="container text-center">
          <h1 className="display-4 text-white animated slideInDown mb-4" data-aos="fade-down">
            Events
          </h1>
          <nav aria-label="breadcrumb animated slideInDown" data-aos="fade-down" data-aos-delay="200">
            <ol className="breadcrumb justify-content-center mb-0">
              <li className="breadcrumb-item">
                <Link to="/" className="text-white">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-white">Pages</span>
              </li>
              <li className="breadcrumb-item text-primary active" aria-current="page">
                Events
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="container-xxl py-5">
        <div className="container">
          <div className="text-center mx-auto mb-5" data-aos="fade-up" data-aos-delay="100" style={{ maxWidth: '720px' }}>
            <div className="d-inline-block rounded-pill bg-secondary text-primary py-1 px-3 mb-3">
              2026 Pen &amp; Notebooks Drive
            </div>
            <h1 className="display-6 mb-3">School Visits</h1>
            <p className="mb-0">
              {isLoadingImages
                ? 'Loading photos from the gallery.'
                : `${eventPhotoCount} photos and ${eventVideoCount} videos from our 2026 pen and notebook distribution, grouped by school visit.`}
            </p>
            {galleryError && <p className="text-danger mt-3 mb-0">{galleryError}</p>}
          </div>

          {isLoadingImages ? (
            <div className="text-center py-5" role="status" aria-live="polite">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading photos</span>
              </div>
            </div>
          ) : (
          <>
          <div className="events-visit-filters mb-4" role="tablist" aria-label="School visits">
            {eventAlbums.map((album) => {
              const isActive = album.id === activeAlbumId;
              return (
                <button
                  key={album.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`events-visit-filter ${isActive ? 'is-active' : ''}`}
                  onClick={() => setActiveAlbumId(album.id)}
                >
                  {album.chip}
                </button>
              );
            })}
          </div>

          {activeAlbum && (
            <div className="events-album-detail">
              <div className="row align-items-end mb-4">
                <div className="col-lg-8">
                  <h2 className="h3 mb-2">{activeAlbum.title}</h2>
                  <p className="mb-0 text-body">{activeAlbum.description}</p>
                </div>
                <div className="col-lg-4 text-lg-end mt-3 mt-lg-0">
                  <span className="events-album-meta">
                    {activeAlbum.items.filter((item) => item.type === 'video').length} videos ·{' '}
                    {activeAlbum.items.filter((item) => item.type === 'image').length} photos
                  </span>
                </div>
              </div>

              <div className="row g-3">
                {visibleItems.map((media) => (
                  <div key={media.id} className="col-6 col-md-4 col-lg-3">
                    <button
                      type="button"
                      className="events-media-card w-100 border-0 p-0"
                      onClick={() => openMedia(media)}
                      aria-label={`Preview ${media.title}`}
                    >
                      <div className="events-thumb-wrapper">
                        {media.type === 'video' ? (
                          <video className="events-thumb-image" src={media.src} muted playsInline preload="metadata" />
                        ) : (
                          <Image
                            className="events-thumb-image"
                            src={media.src}
                            alt={media.title}
                            loading="lazy"
                            responsive={false}
                            transformation={[{ width: 480 }]}
                          />
                        )}
                        {media.type === 'video' && (
                          <span className="events-video-badge" aria-hidden="true">
                            <i className="fa fa-play"></i>
                          </span>
                        )}
                      </div>
                    </button>
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-5">
                  <button type="button" className="btn btn-primary py-2 px-4" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
                    Show more photos
                  </button>
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>
      </div>

      {activeMedia && activeAlbum && (
        <div
          className="events-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Event media preview"
          onClick={closeLightbox}
        >
          <div className="events-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <div className="events-lightbox-toolbar">
              <div>
                <h5 className="text-white mb-0">{activeAlbum.title}</h5>
                <small className="text-white-50">
                  {lightboxIndex + 1} / {activeAlbum.items.length}
                </small>
              </div>
              <button type="button" className="events-lightbox-close" aria-label="Close preview" onClick={closeLightbox}>
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="events-lightbox-stage">
              {activeAlbum.items.length > 1 && (
                <>
                  <button
                    type="button"
                    className="events-lightbox-nav events-lightbox-prev"
                    aria-label="Previous"
                    onClick={() => showLightboxAt(lightboxIndex - 1)}
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                  <button
                    type="button"
                    className="events-lightbox-nav events-lightbox-next"
                    aria-label="Next"
                    onClick={() => showLightboxAt(lightboxIndex + 1)}
                  >
                    <i className="fa fa-chevron-right"></i>
                  </button>
                </>
              )}

              {activeMedia.type === 'video' ? (
                <video className="events-lightbox-media" src={activeMedia.src} controls autoPlay />
              ) : (
                <Image
                  className="events-lightbox-media"
                  src={activeMedia.src}
                  alt={activeMedia.title}
                  responsive={false}
                  transformation={[{ width: 1600 }]}
                />
              )}
            </div>

            {activeAlbum.items.length > 1 && (
              <div className="events-lightbox-thumbs" aria-label="Album photos">
                {activeAlbum.items.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`events-lightbox-thumb ${index === lightboxIndex ? 'is-active' : ''}`}
                    onClick={() => showLightboxAt(index)}
                    aria-label={`Show item ${index + 1}`}
                  >
                    {item.type === 'video' ? (
                      <span className="events-lightbox-thumb-video">
                        <i className="fa fa-play"></i>
                      </span>
                    ) : (
                      <Image
                        src={item.src}
                        alt=""
                        responsive={false}
                        transformation={[{ width: 480 }]}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />

      <button type="button" className="btn btn-lg btn-primary btn-lg-square back-to-top" aria-label="Back to top">
        <i className="bi bi-arrow-up"></i>
      </button>
    </>
  );
};

export default EventsPage;
